import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Image } from '../../../snippets';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

export function ProductImage({ 
  alt, 
  image, 
  onLoad, 
  priority,
  enabledZoom = false,
  cursorStyle,
  isMobileViewport,
  swiperInstance,
}) {
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  // Add state for zoom and transform origin
  const [isZoomed, setIsZoomed] = useState(false);
  const [transformOrigin, setTransformOrigin] = useState('50% 50%');

  // Handle zoom toggle
  const handleZoomToggle = (e) => {
    if (!enabledZoom || isMobileViewport) return;

    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left) / width * 100;
    const y = (e.clientY - top) / height * 100;

    setTransformOrigin(`${x}% ${y}%`);
    setIsZoomed(!isZoomed);
  };

  // Handle mouse move for zoom effect
  const handleMouseMove = (e) => {
    if (!enabledZoom || !isZoomed || isMobileViewport) return;

    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left) / width * 100;
    const y = (e.clientY - top) / height * 100;

    setTransformOrigin(`${x}% ${y}%`);
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setIsZoomed(false);
  };

  // Disable Swiper draggable when zoom is enabled
  useEffect(() => {
    if (swiperInstance) {
      swiperInstance.allowTouchMove = !isZoomed;
    }
  }, [isZoomed, swiperInstance]);

  return (
    <div
      className="relative bg-offWhite aspect-[4/5] overflow-hidden"
      ref={ref}
      onClick={handleZoomToggle}
      onMouseMove={enabledZoom && !isMobileViewport ? handleMouseMove : undefined}
      onMouseLeave={enabledZoom && !isMobileViewport ? handleMouseLeave : undefined}
    >
      {(priority || inView) && image?.src && (
        isMobileViewport ? (
          <TransformWrapper
            doubleClick={{ mode: 'zoomIn' }}
            wheel={{ step: 5 }}
            zoomIn={{ step: 5 }}
            zoomOut={{ step: 5 }}
            initialScale={1}
            minScale={1}
            maxScale={4}
            style={{ width: '100%', height: '100%' }}
            options={{ limitToBounds: false, disabled: true }} // Disable dragging
            panning={{ disabled: true }} // Disable single-finger panning
            pinch={{ step: 5, disabled: false }} // Enable pinch-to-zoom
            onZoomChange={(ref) => {
              if (swiperInstance) {
                swiperInstance.allowTouchMove = ref.state.scale === 1;
              }
            }}
            onPinchingStart={() => {
              if (swiperInstance) {
                swiperInstance.allowTouchMove = false;
              }
            }}
            onPinchingStop={() => {
              if (swiperInstance) {
                swiperInstance.allowTouchMove = true;
              }
            }}
          >
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%' }}
            >
              <img
                alt={alt}
                onLoad={onLoad}
                src={image.src}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </TransformComponent>
          </TransformWrapper>
        ) : (
          <Image
            alt={alt}
            fill
            onLoad={onLoad}
            priority={priority}
            sizes="(min-width: 1440px) 900px, (min-width: 768px) 50vw, 100vw"
            src={image.src}
            srcSetSizes={[480, 960, 1280, 1920]}
            quality={100}
            style={{
              transition: enabledZoom ? 'transform 0.1s ease-out' : 'none',
              transform: isZoomed ? 'scale(2)' : 'scale(1)',
              transformOrigin: transformOrigin,
              cursor: cursorStyle,
            }}
          />
        )
      )}
    </div>
  );
}

ProductImage.displayName = 'ProductImage';